import { useState, useCallback } from "react";
import { Box, VStack, HStack, Icon } from "@chakra-ui/react";
import OutlineEmail from "components/Icon/Iconify/OutlineEmail";
import EmailsTable from "features/Resources/Email/Components/Table/EmailsTable";
import EmailsFiltersComponent, {
  EmailsFilters,
  ViewMode,
  SortMode,
} from "features/Resources/Email/Components/EmailsFilters";
import AppPage from "layouts/AppPage";
import { Heading18 } from "components/Heading";
import useSideDrawer from "hooks/useSideDrawer";
import { useSearchParams } from "react-router";
import { EmailSummary } from "features/Resources/Email/models";
import ActionSideDrawer from "components/ActionSideDrawer";

const defaultFilters: EmailsFilters = {
  view_mode: "week",
};

const sortOptions = [
  { label: "Deliver At: Oldest First", value: "effective_deliver_at_asc" },
  { label: "Deliver At: Newest First", value: "effective_deliver_at_desc" },
];

const defaultSortString = "effective_deliver_at_desc";

export default function EmailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<EmailsFilters>(() => {
    // Initialize filters from URL params
    const initialFilters: EmailsFilters = { ...defaultFilters };
    const q = searchParams.get("q");
    const recipientId = searchParams.get("recipient_id");
    const recipientType = searchParams.get("recipient_type");
    const hasIssues = searchParams.get("has_issues");
    const startDate = searchParams.get("start_date");
    const endDate = searchParams.get("end_date");
    const viewMode = searchParams.get("view_mode") as ViewMode;
    const status = searchParams.get("status");
    const stageKey = searchParams.get("stage_key");
    const sort = searchParams.get("sort") as SortMode;

    if (q) initialFilters.q = q;
    if (recipientId) initialFilters.recipient_id = recipientId;
    if (recipientType) initialFilters.recipient_type = recipientType;
    if (hasIssues) initialFilters.has_issues = hasIssues;
    if (viewMode) initialFilters.view_mode = viewMode;
    if (startDate) initialFilters.start_date = startDate;
    if (endDate) initialFilters.end_date = endDate;
    if (status) initialFilters.status = status;
    if (stageKey) initialFilters.stage_key = stageKey;
    if (sort) initialFilters.sort = sort;

    return initialFilters;
  });

  const handleFiltersChange = useCallback(
    (newFilters: EmailsFilters) => {
      setFilters(newFilters);

      // Update URL params
      const params = new URLSearchParams();
      if (newFilters.q) params.set("q", newFilters.q);
      if (newFilters.recipient_id) params.set("recipient_id", newFilters.recipient_id);
      if (newFilters.recipient_type) params.set("recipient_type", newFilters.recipient_type);
      if (newFilters.has_issues) params.set("has_issues", newFilters.has_issues);
      if (newFilters.status) params.set("status", newFilters.status);
      if (newFilters.stage_key) params.set("stage_key", newFilters.stage_key);
      if (newFilters.sort) params.set("sort", newFilters.sort);
      if (newFilters.view_mode) params.set("view_mode", newFilters.view_mode);
      if (newFilters.view_mode === "week") {
        if (newFilters.start_date) params.set("start_date", newFilters.start_date);
        if (newFilters.end_date) params.set("end_date", newFilters.end_date);
      }

      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    // Set view mode param for reset
    const params = new URLSearchParams();
    params.set("view_mode", defaultFilters.view_mode!);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handleSortChange = useCallback(
    (value: string) => {
      const newFilters = {
        ...filters,
        sort: value || undefined,
      };
      handleFiltersChange(newFilters);
    },
    [filters, handleFiltersChange],
  );

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<EmailSummary>({
    getItemId: (item) => item?.id,
  });

  // Derive current sort with default
  const currentSort = filters.sort || defaultSortString;

  // Build query string from filters with proper filter and sort params
  const queryString = (() => {
    const params = new URLSearchParams();
    let filterIndex = 0;

    // Add search query
    if (filters.q) {
      params.set("q", filters.q);
    }

    // Add has_issues as top-level param
    if (filters.has_issues) {
      params.set("has_issues", filters.has_issues);
    }

    // Add stage_key as top-level param (not a filter)
    if (filters.stage_key) {
      params.set("stage_key", filters.stage_key);
    }

    // Exclude pending new hire journeys (include false and NULL - emails without a journey)
    params.set(`filter[${filterIndex}][field]`, "new_hire_journeys.pending");
    params.set(`filter[${filterIndex}][operator]`, "!=");
    params.set(`filter[${filterIndex}][value]`, "true");
    filterIndex += 1;

    // Add date range filters only if view_mode is "week"
    if (filters.view_mode === "week") {
      if (filters.start_date) {
        params.set(`filter[${filterIndex}][field]`, "deliver_at");
        params.set(`filter[${filterIndex}][operator]`, ">=");
        params.set(`filter[${filterIndex}][value]`, filters.start_date);
        filterIndex += 1;
      }

      if (filters.end_date) {
        params.set(`filter[${filterIndex}][field]`, "deliver_at");
        params.set(`filter[${filterIndex}][operator]`, "<=");
        params.set(`filter[${filterIndex}][value]`, filters.end_date);
        filterIndex += 1;
      }
    }

    // Add recipient filter
    if (filters.recipient_id) {
      params.set(`filter[${filterIndex}][field]`, "recipient_id");
      params.set(`filter[${filterIndex}][operator]`, "=");
      params.set(`filter[${filterIndex}][value]`, filters.recipient_id);
      filterIndex += 1;
    }

    // Add recipient_type filter
    if (filters.recipient_type) {
      params.set(`filter[${filterIndex}][field]`, "recipient_type");
      params.set(`filter[${filterIndex}][operator]`, "=");
      params.set(`filter[${filterIndex}][value]`, filters.recipient_type);
      filterIndex += 1;
    }

    // Add status filter
    if (filters.status) {
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "=");
      params.set(`filter[${filterIndex}][value]`, filters.status);
    }

    // Add sort based on currentSort
    if (currentSort === "effective_deliver_at_asc") {
      params.set("sort[0][sort_by]", "effective_deliver_at");
      params.set("sort[0][direction]", "asc");
    } else if (currentSort === "effective_deliver_at_desc") {
      params.set("sort[0][sort_by]", "effective_deliver_at");
      params.set("sort[0][direction]", "desc");
    }

    return params.toString();
  })();

  return (
    <AppPage>
      <HStack my="4" width="100%" justifyContent="space-between">
        <HStack spacing={2}>
          <Icon as={OutlineEmail} boxSize={5} color="fg.subtle" />
          <Heading18>Emails</Heading18>
        </HStack>
      </HStack>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <EmailsFiltersComponent
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleReset}
            currentSort={currentSort}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            defaultSortString={defaultSortString}
          />

          <EmailsTable queryString={queryString} onExpand={expandClickHandlerByItem} />
        </VStack>
      </Box>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />
    </AppPage>
  );
}
